/**
 * Discussions (community phase 3) — the pure, render-side rules of the READ side: hrefs, URL-input
 * validation, the two top tabs, the sort row, the card's words and chips, the owner badge on an
 * answer, the index rule and the JSON-LD. No fetch, no React; unit-tested in
 * `tests/community-discussions-model.spec.ts`. Contract:
 * `dealhunt/docs/sg-owner-discussions-contract.md` (§3 reads, §7 SoundGuys).
 *
 * What is NOT here: any limit, threshold or point value. Counts, the owner set and "what an answer
 * earns" (`points.reply`, the summary's) are DealHunt's; an absent figure renders nothing, never
 * zero. The writer's rules (limits it words in its checklist) live in `discussions-writer.ts`.
 */
import type { CommentHubRole } from 'shared-code/commenthub-api'
import { withSlash } from 'lib/dealhunt/url'
import {
  COMMUNITY_PATH,
  categoryPathQueryParam,
  clampText,
  communityCategoryHref,
  communityCategoryPath,
  communityHref,
  memberHref,
  safeImageUrl,
  sentCount,
  shortAge,
} from './model'
import type { CommunityCategory, CommunityPoints } from './types'
import type {
  DiscussionIdentityOfThread,
  ImageVariant,
  ImageVariants,
  DiscussionBlock,
  DiscussionCard,
  DiscussionGearChip,
  DiscussionImageStatus,
  DiscussionKind,
  DiscussionListResponse,
  DiscussionOwnerOfLinked,
  DiscussionSort,
  DiscussionThread,
  DiscussionWaitingResponse,
} from './discussions-types'

// ── routes ────────────────────────────────────────────────────────────────────

export const DISCUSSIONS_SEGMENT = 'discussions'
export const DISCUSSIONS_PATH = `${COMMUNITY_PATH}/${DISCUSSIONS_SEGMENT}`
/** The writer (frame 10b) — `pages/community/discussions/new.tsx`, built beside this branch. */
export const NEW_DISCUSSION_PATH = `${DISCUSSIONS_PATH}/new`
/** The author's edit page — `pages/community/discussions/edit/[id].tsx`, built beside this branch.
 *  Keyed by the id alone: a slug changes with the title the page edits. */
export const EDIT_DISCUSSION_PATH = `${DISCUSSIONS_PATH}/edit`

export const DISCUSSION_SORTS: readonly DiscussionSort[] = ['new', 'top', 'unanswered', 'photos']
/** The order every visit opens on; kept out of the URL so the list has one address. */
export const DISCUSSION_DEFAULT_SORT: DiscussionSort = 'new'

/** The list, sorted and optionally filtered: `/community/discussions` (New, every category),
 *  `…?sort=top`, `…?category=earbuds/wireless-earbuds&sort=top`. Sort and category ARE in the URL
 *  here, unlike the review feed's tabs: each is a different list, the same for every reader, so it
 *  is shared-cacheable and linkable (contract §7). ONE spelling per list: `category` (a validated
 *  path, its `/` literal) before `sort`, New and "every category" query-less. */
export function discussionsHref(
  trailingSlash: boolean,
  sort: DiscussionSort = 'new',
  category: string | null = null
): string {
  const base = withSlash(DISCUSSIONS_PATH, trailingSlash)
  const query = [
    category ? `category=${category}` : null,
    sort === DISCUSSION_DEFAULT_SORT ? null : `sort=${sort}`,
  ].filter(Boolean)
  return query.length ? `${base}?${query.join('&')}` : base
}

/** `?sort=` as a sort, or null for anything else (absent, repeated, unknown). */
export function discussionSortParam(raw: unknown): DiscussionSort | null {
  return typeof raw === 'string' && (DISCUSSION_SORTS as readonly string[]).includes(raw)
    ? (raw as DiscussionSort)
    : null
}

/** The list a `/community/discussions` URL asks for, and its one canonical address. */
export interface DiscussionsListQuery {
  sort: DiscussionSort
  /** A category path (`categoryPathQueryParam`) — not yet resolved against `/categories`. */
  category: string | null
  /** The canonical URL when the one asked is another spelling of it (308), else null. */
  redirect: string | null
}

const LIST_PARAMS = ['category', 'sort'] as const

/**
 * `?category=` and `?sort=` of the list, validated BEFORE any DealHunt read. Other spellings 308:
 * `sort=new`, an unknown/repeated/empty value (dropped), a category in another case or with edge
 * slashes (kept, canonicalised), a malformed one (dropped), and the two params in the other order.
 * Compared on Next's DECODED query, key order included: `%2F` and `/` are the same address here —
 * a raw-string check would loop wherever Next re-formats the URL. Other params are not ours.
 */
export function discussionsListQuery(
  query: Readonly<Record<string, string | string[] | undefined>>,
  trailingSlash: boolean
): DiscussionsListQuery {
  const sort = discussionSortParam(query.sort) ?? DISCUSSION_DEFAULT_SORT
  const category = categoryPathQueryParam(query.category)
  const canonical = discussionsHref(trailingSlash, sort, category)
  const askedKeys = Object.keys(query).filter(k => (LIST_PARAMS as readonly string[]).includes(k))
  const asked = askedKeys.map(k => `${k}=${String(query[k])}`).join('&')
  const wanted = canonical.includes('?') ? canonical.slice(canonical.indexOf('?') + 1) : ''
  return { sort, category, redirect: asked === wanted ? null : canonical }
}

/** A thread's address: `/community/discussions/<id>-<slug>` (`<id>` alone when DealHunt sent no
 *  slug). Built from the id and slug, never from the card's `url` field. */
export function discussionHref(
  discussion: Pick<DiscussionCard, 'id' | 'slug'>,
  trailingSlash: boolean
): string {
  const slug = typeof discussion.slug === 'string' ? discussion.slug : ''
  return withSlash(`${DISCUSSIONS_PATH}/${discussion.id}${slug ? `-${slug}` : ''}`, trailingSlash)
}

/**
 * The writer, optionally opened on a kind, with a product pre-linked (its SoundGuys slug, what
 * follows `/product/`), on a category (a community category PATH, `earbuds/wireless-earbuds` —
 * the Discussions tab's CTAs while a category is picked; its `/` stays literal)
 * and with a title filled in (an opener). The writer reads `?kind=`, `?product=`, `?category=`
 * and `?title=`, validates each, and merges them into the draft once (`applyPreset`).
 */
export function newDiscussionHref(
  opts: {
    kind?: DiscussionKind
    product?: string | null
    category?: string | null
    title?: string | null
  } = {},
  trailingSlash = false
): string {
  const qs = new URLSearchParams()
  if (opts.kind) {
    qs.set('kind', opts.kind)
  }
  if (opts.product) {
    qs.set('product', opts.product)
  }
  if (opts.category) {
    qs.set('category', opts.category)
  }
  const title = opts.title?.trim()
  if (title) {
    qs.set('title', title)
  }
  // `/` is legal in a query: a category path (and an opener) reads better with it literal.
  const query = qs.toString().replace(/%2F/gi, '/')
  return withSlash(NEW_DISCUSSION_PATH, trailingSlash) + (query ? `?${query}` : '')
}

/** The author's edit page for one post: `/community/discussions/edit/<id>`, the id lowercased. */
export function discussionEditHref(id: string, trailingSlash: boolean): string {
  return withSlash(`${EDIT_DISCUSSION_PATH}/${encodeURIComponent(id.toLowerCase())}`, trailingSlash)
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/**
 * The `<uuid>-<slug>` segment of a thread URL, lowercased and split, or null when it cannot be
 * one. Checked BEFORE any fetch (a made-up path costs DealHunt nothing); the page then 308s every
 * other spelling (case, an old slug after a retitle) to `discussionHref` of DealHunt's answer.
 */
export function discussionIdSlugParam(raw: unknown): { id: string; slug: string | null } | null {
  if (typeof raw !== 'string' || raw.length > 36 + 1 + 120) {
    return null
  }
  const value = raw.toLowerCase()
  const id = value.slice(0, 36)
  if (!UUID_RE.test(id)) {
    return null
  }
  const rest = value.slice(36)
  if (!rest) {
    return { id, slug: null }
  }
  const slug = rest.startsWith('-') ? rest.slice(1) : ''
  return SLUG_RE.test(slug) ? { id, slug } : null
}

/** A bare thread id (an API route's `:id`), lowercased, or null when it is not a UUID — checked
 *  before any DealHunt fetch, so every spelling is one cache entry. */
export function discussionIdParam(raw: unknown): string | null {
  if (typeof raw !== 'string') {
    return null
  }
  const id = raw.toLowerCase()
  return UUID_RE.test(id) ? id : null
}

/** The id of the thread's answers section — a card's "Answer first" and answer count jump to it. */
export const ANSWERS_ANCHOR = 'answers'

// ── the two top tabs ────────────────────────────────────────────────────────

export type CommunityTabKey = 'reviews' | 'discussions'

export interface CommunityTab {
  key: CommunityTabKey
  label: string
  href: string
  /** DealHunt's total, or null (not sent: the tab shows no count, never a zero). */
  count: number | null
  on: boolean
}

/** The category both tabs are filtered to — the page's node: its path and DealHunt's rolled-up
 *  counts (`/categories`). */
export interface CommunityTabsCategory {
  path: string
  reviews?: unknown
  discussions?: unknown
}

/** The community's two top tabs (frame 10a), LINKS to two pages — never client state, never a
 *  `?tab=` (`/community?tab=discussions` 308s to the path). Counts are the summary's — the
 *  Discussions count shows while the list is early too (Guillaume, 2026-10-09: the number, not a
 *  NEW pill; `discussions_early` still shapes the list itself). Under a category both tabs stay in it (`/community/<path>` · `/community/discussions?category=`
 *  — the sort is the Discussions tab's own, never carried) and count the node, not the site. */
export function communityTabs(
  summary: {
    reviews_total?: unknown
    discussions_total?: unknown
    discussions_early?: unknown
  } | null,
  active: CommunityTabKey,
  trailingSlash: boolean,
  category: CommunityTabsCategory | null = null
): CommunityTab[] {
  const counts = category ?? {
    reviews: summary?.reviews_total,
    discussions: summary?.discussions_total,
  }
  return [
    {
      key: 'reviews',
      label: 'Owner reviews',
      href: category
        ? communityCategoryHref(category.path, trailingSlash)
        : communityHref(trailingSlash),
      count: sentCount(counts.reviews),
      on: active === 'reviews',
    },
    {
      key: 'discussions',
      label: 'Discussions',
      href: discussionsHref(trailingSlash, DISCUSSION_DEFAULT_SORT, category?.path ?? null),
      count: sentCount(counts.discussions),
      on: active === 'discussions',
    },
  ]
}

// ── Early Discussions (2026-10-04) ───────────────────────────────────────────
//
// The launch state of the Discussions tab (design "Discussions Empty State", frame 1a): the hero
// with the two capped badges, the threads waiting for a first reply, the Opener card.
// Every figure (early or not, counts, caps, points, the unanswered total) is DealHunt's and
// optional: absent renders nothing, never zero. The openers are copy — prompts, not figures.

/** DealHunt's call that the discussions are still few (`discussions_early`): the tab reads NEW,
 *  the sort row is hidden, the waiting list leads. SoundGuys never holds the threshold. */
export function discussionsEarly(summary: { discussions_early?: unknown } | null): boolean {
  return summary?.discussions_early === true
}

// The two capped badges' cards and sentences live in `gamification.ts` (re-exported here for
// the discussions surfaces that already import them from this module).
export {
  conversationStarterLine,
  earlyBadgeAbout,
  earlyBadgeCards,
  earlyBadgeGiven,
  earlyBadgeShort,
  type EarlyBadgeCard,
} from './gamification'

/** The phone's "Waiting for a first reply" count (Community mobile M2): the summary's
 *  `discussions_unanswered_total` — the same population as the `scope=all` read (published,
 *  either kind, no answer; contract §1) — else (absent, or a 0 beside rows on the page) how
 *  many rows the list shows. */
export function waitingHeaderCount(total: unknown, shown: number): number {
  return sentCount(total) || shown
}

/** "3 waiting for a first reply" — the summary's `discussions_unanswered_total` (either kind).
 *  Null when absent, and when it is 0: there is nothing to reply to, and the CTA beside it then
 *  leads to the writer. */
export function waitingForReplyLine(total: unknown): string | null {
  const n = sentCount(total)
  return n ? `${n.toLocaleString('en-US')} waiting for a first reply` : null
}

/** The widened waiting list (`?scope=all`), shown only when DealHunt ECHOES the scope — an
 *  older DealHunt ignores the param and answers the product questions, which must not be listed
 *  as "every thread waiting" (Directive 02c8c285). Empty otherwise. */
export function waitingAll(
  response: Pick<DiscussionWaitingResponse, 'items' | 'scope'> | null | undefined
): DiscussionCard[] {
  return response?.scope === 'all' && Array.isArray(response.items) ? response.items : []
}

/** The "Waiting for a first reply" list's anchor on the Discussions tab. */
export const WAITING_ANCHOR = 'waiting'

export interface ReplyCta {
  href: string
  label: string
  /** The small line under the button: where it goes, so the label never over-promises. */
  hint: string
}

/**
 * The Early Discussions hero's button. With the waiting list on the page it leads down to it —
 * the reader picks a thread they can answer (the shared HTML cannot leave out the viewer's own
 * threads, and replying to your own does not count). Without the list on the page (a category
 * picked, or past early) it opens the first waiting thread's answers; with nothing waiting it is
 * "Start a discussion" and opens the writer — the label always names what the click does.
 */
export function replyToDiscussionCta(
  waiting: DiscussionCard[],
  listShown: boolean,
  trailingSlash: boolean
): ReplyCta {
  const [first] = waiting
  if (!first) {
    return {
      href: newDiscussionHref({}, trailingSlash),
      label: 'Start a discussion →',
      hint: 'Nothing is waiting for a reply right now — start one.',
    }
  }
  if (listShown) {
    return {
      href: `#${WAITING_ANCHOR}`,
      label: 'Reply to a discussion ↓',
      hint: 'Takes you to the discussions below still waiting for a first reply.',
    }
  }
  return {
    href: `${discussionHref(first, trailingSlash)}#${ANSWERS_ANCHOR}`,
    label: 'Reply to a discussion →',
    hint: 'Opens a discussion still waiting for a first reply.',
  }
}

/** The Opener card's prompts — SoundGuys copy, hard-coded on purpose: they are questions to
 *  start a thread with, not figures. Each becomes the writer's `?title=`. */
export const DISCUSSION_OPENERS: readonly string[] = [
  'What’s one product you regret buying, and why?',
  'Which feature do you never use?',
  'Help me pick between two pairs',
  'Show us your desk setup',
  'What did you upgrade from, and was it worth it?',
  'Over-ears or earbuds for a long flight?',
  'What’s the oldest pair you still use every week?',
]

/** The Opener card at one rotation step: a featured opener and the next `more` after it, in a
 *  ring. "Shuffle" steps `start` by one — client state only, the shared HTML opens on 0. */
export function openerSet(
  start: number,
  more = 3,
  openers: readonly string[] = DISCUSSION_OPENERS
): { featured: string; more: string[] } {
  const n = openers.length
  const at = ((Math.trunc(start) % n) + n) % n
  return {
    featured: openers[at],
    more: Array.from({ length: Math.min(more, n - 1) }, (_, i) => openers[(at + 1 + i) % n]),
  }
}

/** How many openers the phone list shows (Community mobile M2). */
export const PHONE_OPENERS = 3

/** The phone openers in the shared HTML: the first `n`, always the same — the page is
 *  shared-cached, so the server never picks at random. */
export function firstOpeners(
  n = PHONE_OPENERS,
  openers: readonly string[] = DISCUSSION_OPENERS
): string[] {
  return openers.slice(0, Math.max(0, n))
}

/** The phone openers after mount: `n` distinct openers in a random order (a partial
 *  Fisher–Yates over a copy; `random` returns [0, 1) like Math.random). Fewer when there are
 *  fewer openers. */
export function pickOpeners(
  random: () => number,
  n = PHONE_OPENERS,
  openers: readonly string[] = DISCUSSION_OPENERS
): string[] {
  const pool = [...openers]
  const k = Math.min(Math.max(0, n), pool.length)
  for (let i = 0; i < k; i += 1) {
    const r = Math.min(Math.max(random(), 0), 0.999999)
    const j = i + Math.floor(r * (pool.length - i))
    const swap = pool[i]
    pool[i] = pool[j]
    pool[j] = swap
  }
  return pool.slice(0, k)
}

// ── the sort row ────────────────────────────────────────────────────────────

export interface DiscussionSortTab {
  sort: DiscussionSort
  label: string
  href: string
  on: boolean
  /** Unanswered only: DealHunt's `unanswered_total`, or null. */
  count: number | null
}

const SORT_LABELS: Readonly<Record<DiscussionSort, string>> = {
  new: 'New',
  top: 'Top',
  unanswered: 'Unanswered',
  photos: 'With photos',
}

/** The sort row (10a), as links, inside the category picked (if any). The lit tab is the order DealHunt ECHOED when it answered
 *  (an older DealHunt may serve another order than asked), else the one asked. */
export function discussionSortTabs(
  list: Pick<DiscussionListResponse, 'sort' | 'unanswered_total'> | null,
  asked: DiscussionSort,
  trailingSlash: boolean,
  category: string | null = null
): DiscussionSortTab[] {
  const lit = discussionSortParam(list?.sort) ?? asked
  return DISCUSSION_SORTS.map(sort => ({
    sort,
    label: SORT_LABELS[sort],
    // A sort keeps the category picked: the tabs re-order the list shown, they never widen it.
    href: discussionsHref(trailingSlash, sort, category),
    on: sort === lit,
    count: sort === 'unanswered' ? sentCount(list?.unanswered_total) : null,
  }))
}

// ── the card ────────────────────────────────────────────────────────────────

export function discussionKindLabel(kind: DiscussionKind): string {
  return kind === 'question' ? 'Question' : 'Discussion'
}

/**
 * The "· edited" marker's tooltip from DealHunt's `edited_at` (stamped by the author's PATCH
 * only — a moderator's hide or restore never sets it): "Edited Oct 4, 2026". Date only, en-US,
 * in UTC — the marker is in the shared-cached HTML, so the server and every browser must print
 * the same string (a time of day would differ by zone, and by ICU spacing between Node and
 * browsers). null for a missing or unparseable value: the marker shows without a tooltip.
 */
export function editedTitle(editedAt: unknown): string | null {
  if (typeof editedAt !== 'string' || !editedAt) {
    return null
  }
  const date = new Date(editedAt)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  const day = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
  return `Edited ${day}`
}

/** "3 answers" on a question, "8 replies" on a discussion. */
export function answerCountLine(kind: DiscussionKind, count: number): string {
  const n = count.toLocaleString('en-US')
  if (kind === 'question') {
    return count === 1 ? '1 answer' : `${n} answers`
  }
  return count === 1 ? '1 reply' : `${n} replies`
}

/** "2 from owners", "1 from an owner"; null while none (or unknown). */
export function ownerAnswersLine(count: unknown): string | null {
  const n = sentCount(count)
  if (!n) {
    return null
  }
  return n === 1 ? '1 from an owner' : `${n.toLocaleString('en-US')} from owners`
}

/** "1 owner notified", "4 owners notified"; null while none (or unknown). */
export function ownersNotifiedLine(count: unknown): string | null {
  const n = sentCount(count)
  if (!n) {
    return null
  }
  return n === 1 ? '1 owner notified' : `${n.toLocaleString('en-US')} owners notified`
}

/** "+10" beside "Answer first" — DealHunt's reply rule (`points.reply`, the summary's): an answer
 *  is a reply (contract §5). null without it: nothing is promised. */
export function answerFirstPoints(
  points: Pick<CommunityPoints, 'reply'> | null | undefined
): string | null {
  return points && typeof points.reply === 'number' && points.reply > 0
    ? `+${points.reply.toLocaleString('en-US')}`
    : null
}

/** The card's excerpt: DealHunt's (≤ 200 chars of the first text block), re-clamped in case. */
export function discussionExcerpt(card: Pick<DiscussionCard, 'excerpt'>): string {
  return typeof card.excerpt === 'string' ? clampText(card.excerpt, 200) : ''
}

// ── image sizes ──────────────────────────────────────────────────────────────

/** A size that can go in a `srcset`: https, no whitespace or comma (either would split the
 *  candidate list), positive integer dimensions. */
function usableVariant(v: unknown): ImageVariant | null {
  const c = v as Partial<ImageVariant> | null
  const url = safeImageUrl(c?.url)
  if (
    !c ||
    !url ||
    url.includes(',') ||
    !Number.isSafeInteger(c.width) ||
    !Number.isSafeInteger(c.height) ||
    (c.width as number) <= 0 ||
    (c.height as number) <= 0
  ) {
    return null
  }
  return { url, width: c.width as number, height: c.height as number }
}

/**
 * What an `<img>` draws from one of DealHunt's images: `src` / `width` / `height` from the size
 * `prefer`red (`thumb` on cards, `card` in the thread), `srcSet` over the sizes up to `upTo` —
 * only when DealHunt sent `variants`; without them the plain `url`, no `srcSet` (contract: the
 * sizes are optional on this side). Null when the image has no https URL.
 */
export interface ImageSource {
  src: string
  width: number
  height: number
  srcSet?: string
}

export function imageSource(
  image: { url?: unknown; width?: unknown; height?: unknown; variants?: unknown } | null,
  prefer: 'thumb' | 'card',
  upTo: 'card' | 'full'
): ImageSource | null {
  const full = safeImageUrl(typeof image?.url === 'string' ? image.url : null)
  if (!image || !full) {
    return null
  }
  const width = typeof image.width === 'number' ? image.width : 0
  const height = typeof image.height === 'number' ? image.height : 0
  const plain: ImageSource = { src: full, width, height }
  const variants = image.variants as Partial<ImageVariants> | null | undefined
  const thumb = usableVariant(variants?.thumb)
  const card = usableVariant(variants?.card)
  const first = prefer === 'thumb' ? thumb : card
  if (!first) {
    return plain
  }
  const ladder = prefer === 'thumb' ? [thumb, card] : [card]
  if (upTo === 'full') {
    ladder.push(usableVariant({ url: full, width, height }))
  }
  const seen = new Set<number>()
  const candidates = ladder.filter((v): v is ImageVariant => {
    if (!v || seen.has(v.width)) {
      return false
    }
    seen.add(v.width)
    return true
  })
  return {
    src: first.url,
    width: first.width,
    height: first.height,
    srcSet: candidates.map(v => `${v.url} ${v.width}w`).join(', '),
  }
}

/** `sizes` per layout — the rendered width each slot gets (community.module.css: one column on
 *  a phone, main column ≈ 900 px beside the 320 side column from `lg`). Breakpoints are
 *  `shared-definitions/breakpoints.json`'s (`bp(md)` 768, `bp(lg)` 1024); the widths are hints. */
export const IMAGE_SIZES = {
  /** Card thumbs: a 3-column grid of ≤ 120 px tiles at every width. */
  cardThumb: '120px',
  /** The feed row's large strip (ReviewPhotos `size="large"`): 220 px tall from `md`, 200 px on
   *  a phone, each photo at its own ratio — at most 16:9 wide, never past the row. */
  photoLarge: '(min-width: 768px) 392px, 90vw',
  /** The thread's lead mosaic, by how many photos it shows. */
  mosaicLead: '(min-width: 1024px) 600px, 66vw',
  mosaicSolo: '(min-width: 1024px) 900px, 100vw',
  mosaicPair: '(min-width: 1024px) 450px, 50vw',
  mosaicSmall: '(min-width: 1024px) 300px, 33vw',
  /** Later sets: 2 columns, 3 from `md`. */
  grid: '(min-width: 1024px) 300px, (min-width: 768px) 33vw, 50vw',
} as const

export interface CoverThumb extends ImageSource {
  /** "+N" on the last thumb: the post's images not shown. 0 = none. */
  more: number
}

/** The cover at full size (`url`, never a variant) — the share card and JSON-LD; null without
 *  an https cover. */
export function coverImageUrl(card: Pick<DiscussionCard, 'cover_images'>): string | null {
  const covers = Array.isArray(card.cover_images) ? card.cover_images : []
  return covers.map(image => safeImageUrl(image?.url)).find(Boolean) ?? null
}

/** At most three cover images (https only), the last one carrying "+N" for the rest of
 *  DealHunt's `image_count`. */
export function coverThumbs(
  card: Pick<DiscussionCard, 'cover_images' | 'image_count'>
): CoverThumb[] {
  const shown = (Array.isArray(card.cover_images) ? card.cover_images : [])
    .flatMap(image => {
      const source = imageSource(image, 'thumb', 'card')
      return source ? [{ ...source, more: 0 }] : []
    })
    .slice(0, 3)
  const total = sentCount(card.image_count) ?? shown.length
  const rest = total - shown.length
  if (shown.length && rest > 0) {
    shown[shown.length - 1] = { ...shown[shown.length - 1], more: rest }
  }
  return shown
}

export type GearChipView =
  | { kind: 'product'; key: string; name: string; href: string | null; image: string | null }
  | { kind: 'brand'; key: string; name: string }

/** The linked gear as chips: the first two, then "+N" for the rest (`more`). A product links its
 *  product page; a brand is named, labelled "brand", unlinked. */
export function gearChips(
  gear: DiscussionGearChip[] | null | undefined,
  trailingSlash: boolean,
  max = 2
): { chips: GearChipView[]; more: number } {
  const all = (Array.isArray(gear) ? gear : []).map((chip, i): GearChipView => {
    if (chip.kind === 'product') {
      return {
        kind: 'product',
        key: `p:${chip.node_id}`,
        name: chip.name,
        href: chip.slug ? withSlash(`/product/${chip.slug}`, trailingSlash) : null,
        image: safeImageUrl(chip.image_url),
      }
    }
    return { kind: 'brand', key: `b:${chip.slug ?? chip.name}:${i}`, name: chip.name }
  })
  return { chips: all.slice(0, max), more: Math.max(0, all.length - max) }
}

/** The author's handle links their member page only when they are a member (a public profile). */
export function discussionAuthorHref(
  author: Pick<DiscussionCard['author'], 'handle' | 'is_member'>,
  trailingSlash: boolean
): string | null {
  return author.is_member ? memberHref(author.handle, trailingSlash) : null
}

/** A discussion's category step: the Discussions list filtered to its category (where the
 *  thread is listed — not the category's owner-review page), or the name alone when DealHunt
 *  sent no taxonomy path (Directive 398049d8: never the catalog's page). */
export function discussionCategoryCrumb(
  category: CommunityCategory | null | undefined,
  trailingSlash: boolean
): { label: string; href?: string } | null {
  if (!category) {
    return null
  }
  const path = communityCategoryPath(category.full_slug)
  return path
    ? { label: category.name, href: discussionsHref(trailingSlash, DISCUSSION_DEFAULT_SORT, path) }
    : { label: category.name }
}

// ── answers: the owner badge ────────────────────────────────────────────────

/** True when the answer's author owns linked gear: commentHub's `authorSubjectHash` is a key of
 *  DealHunt's `owner_hashes`. Derived at render, never stored (contract §0). No hash → false. */
export function isOwnerAnswer(
  hash: string | null | undefined,
  ownerHashes: Record<string, DiscussionOwnerOfLinked> | null | undefined
): boolean {
  return (
    typeof hash === 'string' &&
    hash !== '' &&
    Boolean(ownerHashes) &&
    Object.prototype.hasOwnProperty.call(ownerHashes, hash)
  )
}

// ── the SoundGuys team ──────────────────────────────────────────────────────

/** The commentHub roles that are the SoundGuys team: moderators, admins and `staff` (the writers).
 *  `trusted` is a reader. */
export const TEAM_ROLES: readonly CommentHubRole[] = ['moderator', 'admin', 'staff']

/** True when a comment author's commentHub role is a team role — THE source of the SOUNDGUYS TEAM
 *  pill on every comment surface (`components/community/teamRoleBadge`, 2026-10-23). Until then the
 *  pill was `authorSubjectHash` ∩ DealHunt's `staff_hashes`, which only knows team members who
 *  claimed an owner profile; the team's writers never do. `staff_hashes` still feeds the @mention
 *  handles, the team status line and the card footers. */
export function isTeamRole(role: CommentHubRole | null | undefined): boolean {
  return role !== null && role !== undefined && TEAM_ROLES.includes(role)
}

/** The reply author's identity from DealHunt's `identities` map (contract B.2): the entry for
 *  commentHub's `authorSubjectHash`, or null — no hash, no map (older DealHunt), or not a member.
 *  Presentation only: it never earns a pill (the team pill is commentHub's role alone — a member's
 *  Google name is never an impersonation path, ticket §3.3). */
export function identityFor(
  hash: string | null | undefined,
  identities: Record<string, DiscussionIdentityOfThread> | null | undefined
): DiscussionIdentityOfThread | null {
  if (typeof hash !== 'string' || hash === '' || !identities) {
    return null
  }
  return Object.prototype.hasOwnProperty.call(identities, hash) ? identities[hash] : null
}

/** Fallback name for a hidden profile without a handle — never commentHub's (Google) name. */
export const HIDDEN_MEMBER_NAME = 'Member'

/**
 * The commentHub frame's `author` answer for one reply (ticket §3.3 "Replies"): `shown` → the
 * member's name and photo, `hidden` → the handle with the widget's initials (never the Google
 * name or picture commentHub still carries), absent → null = commentHub's own. One mapping for
 * the discussion answers and the review thread's replies.
 */
export function replyAuthorOf(
  hash: string | null | undefined,
  identities: Record<string, DiscussionIdentityOfThread> | null | undefined
): { name: string; avatarUrl: string | null } | null {
  const identity = identityFor(hash, identities)
  if (!identity) {
    return null
  }
  if (identity.kind === 'shown') {
    return { name: identity.name, avatarUrl: identity.avatar_url || null }
  }
  return { name: identity.handle_display || identity.handle || HIDDEN_MEMBER_NAME, avatarUrl: null }
}

/** The pill's text — reader-facing "SoundGuys team", never "staff". */
export const TEAM_PILL = 'SoundGuys team'

/** The team has answered this thread: DealHunt sent `staff_answer_count` > 0. */
export function teamAnswered(card: Pick<DiscussionCard, 'staff_answer_count'>): boolean {
  return (sentCount(card.staff_answer_count) ?? 0) > 0
}

/** An age as a reader says it: "2m ago", "3d ago" — or the month ("Oct 2026") past five weeks,
 *  which takes no "ago". Empty for an unparseable date. */
function agoText(iso: string, now: number): string {
  const age = shortAge(iso, now)
  return /^\d+[mhdw]$/.test(age) ? `${age} ago` : age
}

/** A card's "Team asked · 40m ago": the team was asked (`team_mentioned_at`) and has not answered
 *  yet. Independent of the answer count — three readers' answers do not answer for the team. */
export function teamAskedLine(
  card: Pick<DiscussionCard, 'staff_answer_count' | 'team_mentioned_at'>,
  now: number
): string | null {
  if (teamAnswered(card) || typeof card.team_mentioned_at !== 'string') {
    return null
  }
  const ago = agoText(card.team_mentioned_at, now)
  return ago ? `Team asked · ${ago}` : null
}

/** The thread's quiet status line beside the pill: "mentioned 2m ago · usually answers within
 *  1–2 days" while the team has not answered; "mentioned 2m ago" once it has. Null when the team
 *  was never asked (or the date does not parse). */
export function teamMentionedLine(
  thread: Pick<DiscussionCard, 'staff_answer_count' | 'team_mentioned_at'>,
  now: number
): string | null {
  if (typeof thread.team_mentioned_at !== 'string') {
    return null
  }
  const ago = agoText(thread.team_mentioned_at, now)
  if (!ago) {
    return null
  }
  return teamAnswered(thread)
    ? `mentioned ${ago}`
    : `mentioned ${ago} · usually answers within 1–2 days`
}

/** The pill on an owner's answer: "Owns WH-1000XM4 · 1–2 years" only when both arrive, "Owns
 *  WH-1000XM4" without a duration, "Owner" without either. */
export function ownerAnswerLabel(owner: DiscussionOwnerOfLinked | null | undefined): string {
  const product = owner?.product_name?.trim()
  const duration = owner?.duration_label?.trim()
  if (product && duration) {
    return `Owns ${product} · ${duration}`
  }
  return product ? `Owns ${product}` : 'Owner'
}

/** The answer field's prompt (10c). Never gated on ownership, even with linked gear: anyone
 *  with a point of view may answer. */
export function answerPlaceholder(thread: Pick<DiscussionThread, 'kind' | 'author'>): string {
  const handle = thread.author.handle
  if (thread.kind === 'question') {
    return `Answer ${handle}…`
  }
  return `Reply to ${handle}…`
}

/**
 * "3 answers" / "No answers yet" above the list — DealHunt's `answer_count`, never commentHub's
 * total. DealHunt counts visible comments by anyone but the asker (top-level or reply), so the
 * widget's own total can be higher (the asker's follow-ups); the two are not reconciled.
 */
export function answersTitle(kind: DiscussionKind, sent: unknown): string {
  const count = sentCount(sent)
  if (!count) {
    return kind === 'question' ? 'No answers yet' : 'No replies yet'
  }
  return answerCountLine(kind, count)
}

/**
 * DealHunt registers a new discussion's commentHub container on its worker tick — up to ~10
 * minutes after publish (DealHunt, 2026-10-04). Until then the widget's lookup finds no post.
 * The window the thread page waits it out in (the coordinator's 15 min: DealHunt's 10 + margin).
 */
export const ANSWERS_OPENING_MS = 15 * 60 * 1000

/**
 * The thread is young and commentHub still has no list for it (`total` null after the widget had
 * time to load): the answers say "Answers open in a few minutes" instead of an empty or failing
 * box. An unparseable date or an older post never waits.
 */
export function answersOpening(publishedAt: unknown, now: number, total: number | null): boolean {
  if (total !== null || typeof publishedAt !== 'string') {
    return false
  }
  const at = Date.parse(publishedAt)
  return Number.isFinite(at) && now - at >= -60_000 && now - at < ANSWERS_OPENING_MS
}

export const ANSWERS_OPENING_LINE = 'Answers open in a few minutes'

/** "5 owners were notified. 2 have answered." — null while nobody was notified (or unknown). */
export function ownersNotifiedSentence(notified: unknown, answered: unknown): string | null {
  const n = sentCount(notified)
  if (!n) {
    return null
  }
  const head =
    n === 1 ? '1 owner was notified.' : `${n.toLocaleString('en-US')} owners were notified.`
  const a = sentCount(answered)
  if (a === null) {
    return head
  }
  if (a === 0) {
    return `${head} None has answered yet.`
  }
  return `${head} ${a.toLocaleString('en-US')} ${a === 1 ? 'has' : 'have'} answered.`
}

/** "Answer first" on a question nobody has answered, "Reply first" on a discussion. */
/** The phone Reply button (Community mobile M2): "Reply · +10" with the points a reply earns
 *  (`answerFirstPoints` — the summary's `points.reply`), plain "Reply" without them. */
export function replyButtonLabel(
  points: Pick<CommunityPoints, 'reply'> | null | undefined
): string {
  const pts = answerFirstPoints(points)
  return pts ? `Reply · ${pts}` : 'Reply'
}

export function answerFirstLabel(kind: DiscussionKind): string {
  return kind === 'question' ? 'Answer first' : 'Reply first'
}

/** The commentHub container a thread's answers live on (contract §5). */
export function discussionContainerId(id: string): string {
  return `discussion:${id}`
}

// ── the thread: body, index rule, title, JSON-LD ─────────────────────────────

/** A text block's paragraphs (split on blank lines, contract §2). */
export function textParagraphs(body: string): string[] {
  return body
    .split(/\n\s*\n/)
    .map(p => p.trim())
    .filter(Boolean)
}

export type TextRun = { text: string } | { text: string; href: string }

const URL_IN_TEXT = /\bhttps?:\/\/[^\s<>"']+/g

/** A paragraph cut into plain runs and auto-detected http(s) links (trailing punctuation stays
 *  text — "…" too: DealHunt's cut excerpts glue it to their last word, a URL included). The page
 *  renders the links `rel="ugc nofollow noopener"`. */
export function linkRuns(text: string): TextRun[] {
  const runs: TextRun[] = []
  let last = 0
  for (const match of Array.from(text.matchAll(URL_IN_TEXT))) {
    const start = match.index ?? 0
    const raw = match[0]
    const href = raw.replace(/[.,;:!?)\]}'"…]+$/, '')
    if (start > last) {
      runs.push({ text: text.slice(last, start) })
    }
    runs.push({ text: href, href })
    last = start + href.length
  }
  if (last < text.length) {
    runs.push({ text: text.slice(last) })
  }
  return runs
}

/** A link block's target: https only (contract §2), or null — then the block is not rendered. */
export function safeLinkUrl(url: unknown): string | null {
  if (typeof url !== 'string') {
    return null
  }
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' ? parsed.href : null
  } catch {
    return null
  }
}

/** All the text a reader sees in the body (text, headings, captions), one string. */
export function discussionBodyText(blocks: DiscussionBlock[] | null | undefined): string {
  return (Array.isArray(blocks) ? blocks : [])
    .flatMap(block => {
      if (block.type === 'text') {
        return [block.body]
      }
      if (block.type === 'heading') {
        return [block.text]
      }
      if (block.type === 'images' && block.caption) {
        return [block.caption]
      }
      return []
    })
    .join('\n\n')
}

/** Below this many characters of body text a thread is "thin" — not worth an index entry on its
 *  own (an SEO rule, SoundGuys': the answers are client-rendered and add nothing for a crawler). */
export const DISCUSSION_THIN_CHARS = 140

/** A thread is indexable once it has an answer AND carries a body worth indexing (contract §7);
 *  a hidden thread, an unknown count or a thin body stays `noindex, follow`. */
export function discussionIndexable(
  thread: Pick<DiscussionThread, 'status' | 'answer_count' | 'blocks'>
): boolean {
  if (thread.status !== 'published' || !(sentCount(thread.answer_count) ?? 0)) {
    return false
  }
  return (
    Array.from(discussionBodyText(thread.blocks).replace(/\s+/g, ' ').trim()).length >=
    DISCUSSION_THIN_CHARS
  )
}

export function discussionTitle(thread: Pick<DiscussionThread, 'title' | 'kind'>): string {
  return clampText(thread.title, 90)
}

export function discussionDescription(
  thread: Pick<DiscussionThread, 'title' | 'kind' | 'excerpt' | 'author'>
): string {
  const lead = thread.kind === 'question' ? 'A question' : 'A discussion'
  const excerpt = thread.excerpt ? ` “${clampText(thread.excerpt, 120)}”` : ''
  return `${lead} from ${thread.author.handle} in the SoundGuys community.${excerpt}`
}

/** User text inside a `<script type="application/ld+json">`: the shared serializer
 *  (`LdJsonList`, `JSON.stringify`) does not escape `<`, so a `</script>` in a title would end
 *  the tag. `<` becomes the full-width sign — the metadata reads the same. */
function ldText(text: string): string {
  return text.replace(/</g, '＜')
}

/**
 * The thread's structured data: `QAPage` (a `Question` main entity) for a question,
 * `DiscussionForumPosting` for a discussion. The question/post only — the answers are
 * commentHub's and client-rendered, so no `acceptedAnswer` / `suggestedAnswer` / `comment` is
 * claimed. `url` is absolute. Nothing for a hidden thread.
 */
export function discussionJsonLd(
  thread: DiscussionThread,
  url: string,
  appUrl: string,
  trailingSlash: boolean
): Record<string, unknown>[] {
  if (thread.status !== 'published') {
    return []
  }
  const authorHref = discussionAuthorHref(thread.author, trailingSlash)
  const author: Record<string, unknown> = {
    '@type': 'Person',
    name: ldText(thread.author.handle),
    ...(authorHref ? { url: `${appUrl}${authorHref}` } : {}),
  }
  const text = ldText(clampText(discussionBodyText(thread.blocks), 1000))
  const helpful = sentCount(thread.helpful_count)
  const answers = sentCount(thread.answer_count)
  const image = coverImageUrl(thread)
  if (thread.kind === 'question') {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'QAPage',
        url,
        mainEntity: {
          '@type': 'Question',
          name: ldText(thread.title),
          ...(text ? { text } : {}),
          datePublished: thread.published_at,
          ...(thread.edited_at ? { dateModified: thread.edited_at } : {}),
          author,
          ...(answers !== null ? { answerCount: answers } : {}),
          ...(helpful !== null ? { upvoteCount: helpful } : {}),
          ...(image ? { image } : {}),
        },
      },
    ]
  }
  const stats = [
    helpful !== null && {
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/LikeAction',
      userInteractionCount: helpful,
    },
    answers !== null && {
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/CommentAction',
      userInteractionCount: answers,
    },
  ].filter(Boolean)
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'DiscussionForumPosting',
      url,
      headline: ldText(thread.title),
      ...(text ? { text } : {}),
      datePublished: thread.published_at,
      ...(thread.edited_at ? { dateModified: thread.edited_at } : {}),
      author,
      ...(stats.length ? { interactionStatistic: stats } : {}),
      ...(image ? { image } : {}),
    },
  ]
}

// ── the product page module ─────────────────────────────────────────────────

/** "Questions from readers" on a PDP: DealHunt's items, only when it ECHOES the product asked
 *  (an older DealHunt ignores `?product=` and answers the whole list — never shown under a
 *  product), at most `max`. Empty → the module and its nav entry are left out. */
export function productQuestions(
  list: Pick<DiscussionListResponse, 'items' | 'product'> | null | undefined,
  nodeId: string,
  max = 3
): DiscussionCard[] {
  if (!list || list.product !== nodeId || !Array.isArray(list.items)) {
    return []
  }
  return list.items.slice(0, max)
}

/** A root-relative link DealHunt sent (an owner quote's `url`), or null — never another origin:
 *  no `//host`, and no backslash anywhere (browsers read `/\host` as `//host`). */
export function rootHref(url: unknown): string | null {
  return typeof url === 'string' && /^\/(?!\/)[^\s\\]*$/.test(url) ? url : null
}

// ── the author's own view (keyed `…/discussions/:id/mine`, never in the HTML) ─────────────────

/**
 * The author's own projection out of `GET …/discussions/:id/mine`'s body, for THIS thread (ids
 * compare case-blind: the page serves the lowercase id) — null for anything else: another id, no
 * blocks array, a junk body. Tolerant of any shape, like every keyed read.
 */
export function ownDiscussionFrom(body: unknown, id: string): DiscussionThread | null {
  const discussion = (body as { discussion?: unknown } | null)?.discussion as
    | (Partial<DiscussionThread> & Record<string, unknown>)
    | null
    | undefined
  if (
    !id ||
    !discussion ||
    typeof discussion !== 'object' ||
    typeof discussion.id !== 'string' ||
    discussion.id.toLowerCase() !== id.toLowerCase() ||
    !Array.isArray(discussion.blocks)
  ) {
    return null
  }
  return discussion as DiscussionThread
}

/**
 * Which thread the page draws from: the author's own projection when it arrived for this thread
 * (own wins — it carries every photo they attached, with its status, and their latest edit), else
 * the public one. `pub` null = the public read is hidden; the author still gets theirs.
 *
 * What follows the own projection (widened 2026-10-05 from blocks only, d15c4795): the blocks,
 * the title, the "· edited" marker (`DiscussionThreadBody`) and the crumb's category (the thread
 * page). Kind, author line, counts and the rail's gear cards stay the public read's — `/mine`
 * carries gear chips, not DealHunt's gear cards. Effect-only: never in the shared HTML.
 */
export function mergeOwnProjection(
  pub: DiscussionThread | null,
  own: DiscussionThread | null | undefined
): DiscussionThread | null {
  if (!own || !Array.isArray(own.blocks)) {
    return pub
  }
  if (pub && own.id?.toLowerCase() !== pub.id.toLowerCase()) {
    return pub
  }
  return own
}

/**
 * The author's delete confirmation (the "⋯" menu): deleting removes the whole thread, answers
 * included (Guillaume 2026-10-04 — DealHunt marks the post deleted). The count is DealHunt's
 * `answer_count` from the shared-cached thread, so it can lag: a sent count above 0 is named, and
 * 0 or nothing sent never claims "no answers" — the line says "any answers" instead.
 */
export function deleteDiscussionLine(kind: DiscussionKind, answerCount: unknown): string {
  const post = kind === 'question' ? 'question' : 'post'
  const n =
    typeof answerCount === 'number' && Number.isFinite(answerCount) ? Math.floor(answerCount) : 0
  let answers = 'It is removed for everyone, with any answers.'
  if (n === 1) {
    answers = 'Its answer will be deleted with it.'
  } else if (n > 1) {
    answers = `Its ${n.toLocaleString('en-US')} answers will be deleted with it.`
  }
  return `Delete this ${post}? ${answers} This can't be undone.`
}

/** The marker on a photo only its author sees: `pending` waits for a moderator, `hidden` was not
 *  approved. */
export interface ImageStatusMarker {
  tone: Exclude<DiscussionImageStatus, 'public'>
  label: string
}

/**
 * An image's marker from its `status` (the author's own projection only — public reads never send
 * one): "Not approved" for `hidden` (a report or a moderator took it down); `public`, `pending`,
 * absent or unknown → none, drawn normally. The photo approval gate is off (contract-review-photos
 * §5): photos are public on upload, so nothing says "Awaiting review" any more.
 */
export function imageStatusMarker(status: unknown): ImageStatusMarker | null {
  if (status === 'hidden') {
    return { tone: 'hidden', label: 'Not approved' }
  }
  return null
}
